import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TenantDivisionListItemSchema } from '@/entities/tenants-divisions/api/generated/model';
import { getAvailableYears } from '@/entities/tenants-divisions/lib/divisions-by-year';
import type { TenantUserListItemSchema } from '@/entities/tenantusers/api/generated/model';
import { getCurrentFiscalYear } from '@/shared/lib/fiscal-year';
import {
  Button,
  Form,
  Grid,
  InputField,
  LabeledText,
  SelectField,
  Switch,
  Text,
  Tooltip,
} from '@/shared/ui';
import { FormFieldWrapper } from '@/shared/ui/form/components/FormFieldWrapper';
import { SideMenuFormFooter } from '@/widgets/right-side-menu/ui/SideMenuFormFooter';
import { DivisionsByYearField } from './DivisionsByYearField';
import { type UserDivisionEntry, type UserEditFormValues, userEditSchema } from './schema';
import { useUserEditMutation } from './useUserEditMutation';

type SelectOption = { label: string; value: string };

type Props = {
  user: TenantUserListItemSchema;
  onClose: () => void;
  divisionItems: TenantDivisionListItemSchema[];
  roleOptions: SelectOption[];
};

/**
 * 既存ユーザーの divisions を年度昇順のフォーム値にマップする。
 * 空の場合は当年度（または利用可能な最新年度）の空エントリを 1 つ返す。
 */
const buildInitialDivisions = (
  userDivisions: TenantUserListItemSchema['divisions'],
  availableYears: string[],
): UserDivisionEntry[] => {
  if (userDivisions.length > 0) {
    return [...userDivisions]
      .sort((a, b) => Number(a.year) - Number(b.year))
      .map((d) => ({ year: String(d.year), division_id: d.id }));
  }
  const currentFiscalYear = String(getCurrentFiscalYear());
  const fallbackYear = availableYears.includes(currentFiscalYear)
    ? currentFiscalYear
    : (availableYears.at(-1) ?? currentFiscalYear);
  return [{ year: fallbackYear, division_id: '' }];
};

export const UserEditForm = ({ user, onClose, divisionItems, roleOptions }: Props) => {
  const { t } = useTranslation();

  // 自分自身は無効化不可
  const lockActiveOnly = user.is_self;

  // React Compiler が自動メモ化するため useMemo は使用しない
  const availableYears = getAvailableYears(divisionItems);
  const initialDivisions = buildInitialDivisions(user.divisions, availableYears);

  const form = useForm<UserEditFormValues>({
    resolver: zodResolver(userEditSchema),
    defaultValues: {
      name: user.name,
      divisions: initialDivisions,
      role: user.role,
      is_active: user.is_active,
    },
  });

  const editMutation = useUserEditMutation({ onSuccess: onClose });
  const isPending = editMutation.isPending;

  const handleSubmit = (values: UserEditFormValues) => {
    editMutation.mutate({
      userId: user.id,
      data: {
        name: values.name,
        division_ids: values.divisions.map((d) => d.division_id),
        role: values.role,
        is_active: values.is_active,
      },
    });
  };

  return (
    <Form.Provider {...form}>
      <Form
        leavePageAlertCondition={{
          dirtyFields: form.formState.dirtyFields,
          isSubmitting: form.formState.isSubmitting,
          isSubmitSuccessful: form.formState.isSubmitSuccessful,
        }}
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          <Grid
            columns={1}
            items={[
              [
                {
                  key: 'email',
                  field: (
                    <LabeledText label={t('user-management.field.email')}>{user.email}</LabeledText>
                  ),
                },
                {
                  key: 'name',
                  field: (
                    <InputField
                      control={form.control}
                      name="name"
                      label={t('user-management.field.name')}
                      required
                      autoComplete={false}
                    />
                  ),
                },
                {
                  key: 'divisions',
                  field: <DivisionsByYearField divisionItems={divisionItems} />,
                },
                {
                  key: 'role',
                  field: (
                    <Tooltip
                      tooltipContent={t('user-management.users.edit.cannot-change-own-role')}
                      enabled={lockActiveOnly}
                    >
                      <SelectField
                        control={form.control}
                        name="role"
                        label={t('user-management.field.role')}
                        options={roleOptions}
                        required
                        disabled={lockActiveOnly}
                      />
                    </Tooltip>
                  ),
                },
                {
                  key: 'is_active',
                  field: (
                    <FormFieldWrapper control={form.control} name="is_active" label={null}>
                      {({ field, fieldId }) => (
                        <div className="flex w-full flex-col items-start gap-2">
                          <div className="flex items-center gap-1">
                            <Text htmlFor={fieldId} variant="label">
                              {t('common.fields.status')}
                            </Text>
                            <Tooltip
                              tooltipContent={t('user-management.field.status-tooltip')}
                              contentClassName="whitespace-pre-line"
                            >
                              <span className="inline-flex">
                                <QuestionMarkCircleIcon className="size-3.5 shrink-0 text-text-muted" />
                              </span>
                            </Tooltip>
                          </div>
                          <div className="flex items-center gap-2">
                            <Tooltip
                              tooltipContent={t(
                                'user-management.users.edit.cannot-deactivate-self',
                              )}
                              enabled={lockActiveOnly}
                            >
                              {/* disabled な要素はホバーイベントが届かないため span でラップ */}
                              <span className="inline-flex">
                                <Switch
                                  id={fieldId}
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={lockActiveOnly}
                                />
                              </span>
                            </Tooltip>
                            <Text>
                              {field.value
                                ? t('user-management.status.active')
                                : t('user-management.status.inactive')}
                            </Text>
                          </div>
                        </div>
                      )}
                    </FormFieldWrapper>
                  ),
                },
              ],
            ]}
          />
        </div>
        <SideMenuFormFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" disabled={isPending}>
            {t('common.actions.save')}
          </Button>
        </SideMenuFormFooter>
      </Form>
    </Form.Provider>
  );
};
